import { createSelector } from '@reduxjs/toolkit';
import { publicApi } from './index';
import { setLastUpdated, upsertTokens } from '../../slices/tokenSlice';

export const publicTokenApi = publicApi
  .enhanceEndpoints({
    addTagTypes: ['Price'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getTokenList: builder.query({
        query: () => ({
          url: '/tokens',
          method: 'GET',
        }),
      }),
      getDefaultTokens: builder.query({
        query: () => ({
          url: '/tokens/v2',
          method: 'GET',
        }),
        async onQueryStarted(arg, { queryFulfilled, dispatch }) {
          const { data } = await queryFulfilled;
          dispatch(upsertTokens({ tokens: data.tokens }));
          dispatch(setLastUpdated(Date.now()));
        },
      }),
      searchTokens: builder.query({
        query: ({
          addresses,
          name = '',
          symbol = '',
          category = '',
          pageNo = 0,
          pageAmount = 50,
        }) => {
          const params = new URLSearchParams();

          if (addresses && addresses.length > 0) {
            params.append('searchAddresses', addresses.join(','));
          }

          if (name) {
            params.append('name', name);
          }

          if (symbol) {
            params.append('symbol', symbol);
          }

          if (category && category !== 'ALL') {
            params.append('category', category);
          }

          params.append('pageNo', pageNo.toString());
          params.append('pageAmount', pageAmount.toString());

          return {
            url: `/tokens/v2?${params.toString()}`,
            method: 'GET',
          };
        },
        async onQueryStarted(arg, { queryFulfilled, dispatch }) {
          const { data } = await queryFulfilled;
          dispatch(upsertTokens({ tokens: data.tokens }));
        },
      }),
      getTokenPrice: builder.query({
        query: (address) => ({
          url: `/tokens/${address}/price`,
          method: 'GET',
        }),
        providesTags: (_result, _error, address) => [
          { type: 'Price', id: address },
        ],
      }),
      getTokenPrices: builder.query({
        query: (addresses) => ({
          url: '/tokens/prices',
          method: 'POST',
          body: { addresses },
        }),
        providesTags: (_result, _error, addresses) =>
          addresses.map((address) => ({ type: 'Price', id: address })),
      }),
    }),
  });

export const {
  useGetTokenListQuery,
  useLazyGetTokenPriceQuery,
  useGetDefaultTokensQuery,
  useGetTokenPriceQuery,
  useGetTokenPricesQuery,
  useLazyGetTokenPricesQuery,
  useLazySearchTokensQuery,
  useSearchTokensQuery,
} = publicTokenApi;

// Selectors
const selectTokenList = (state) =>
  publicTokenApi.endpoints.getTokenList.select()(state);

export const selectTokenDetails = createSelector(
  [selectTokenList, (state, address) => address],
  (tokenListResult, address) => {
    return tokenListResult?.data?.tokens?.find(
      (token) => token.address === address,
    );
  },
);

export const selectTokenPrice = (state, address) => {
  const queryArg = address;
  const queryResult =
    publicTokenApi.endpoints.getTokenPrice.select(queryArg)(state);
  console.log(publicTokenApi.endpoints.getTokenPrice);
  return queryResult?.data?.priceUSDC;
};

export const selectTokenPrices = (state, addresses) => {
  const queryArg = addresses;
  const queryResult =
    publicTokenApi.endpoints.getTokenPrices.select(queryArg)(state);
  return queryResult?.data;
};

export const selectIsTokenPriceLoading = (state, address) => {
  const queryArg = address;
  const queryResult =
    publicTokenApi.endpoints.getTokenPrice.select(queryArg)(state);
  return queryResult?.isLoading;
};

export const selectIsTokenPricesLoading = (state, addresses) => {
  const queryArg = addresses;
  const queryResult =
    publicTokenApi.endpoints.getTokenPrices.select(queryArg)(state);
  return queryResult?.isLoading;
};
